import React, { useState, useEffect } from 'react';
import { X, Users, Car, Clock, ChevronDown, Plus, MapPin } from 'lucide-react';
import { Customer, CustomerVehicle, getCustomer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import { createMobileBooking } from '../lib/bookings';
import SelectResourceModal from './manage/SelectResourceModal';
import EditVehicleModal from './booking/EditVehicleModal';
import EditCustomerModal from './manage/EditCustomerModal';
import { Service, ServiceAddon } from '../lib/services';
import SelectCustomerModal from './manage/SelectCustomerModal';
import EditServiceModal from './booking/EditServiceModal';
import CreateVehicleModal from './CreateVehicleModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import SelectServiceModal from './manage/SelectServiceModal';

import { supabase } from '../lib/supabase';
import SelectServiceAddonsModal from './manage/SelectServiceAddonsModal';
import { format, getDaysInMonth } from 'date-fns';
import AddressAutocomplete from './AddressAutocomplete';
import { useEventDrivenModalPersistence } from '../utils/modalPersistence';
import { triggerBookingAutomations } from '../lib/automationsV2';

// Update Service interface to include duration_minutes
interface ExtendedService extends Service {
  duration_minutes?: number;
  tax_rate?: number;
  tax_exempt?: boolean;
}

interface CreateMobileBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSwitchToLocation?: () => void;
}

// Update the interfaces to match the database structure
interface Shift {
  start: string;
  end: string;
}

interface DaySchedule {
  radius: number;
  shifts: Shift[];
  enabled: boolean;
  postal_codes?: string[]; // Array of postal codes that can be serviced on this day
}

interface WorkingHours {
  [day: string]: DaySchedule;
}

// Interface for mobile availability
interface MobileAvailability {
  id: string;
  resource_id: string;
  business_id: string;
  mobile_available: boolean;
  working_hours: WorkingHours;
  postal_codes: {
    [day: string]: string[];
  };
  created_at: string;
  updated_at: string;
}

// Update the Resource interface
interface Resource {
  id: string;           // This is the mobile_availability record ID
  resource_id: string;  // This is the ID from the resources table
  name: string;
  business_id: string;
  mobile_available: boolean;
  working_hours: WorkingHours;
  postal_codes: {
    [day: string]: string[];
  };
  created_at?: string;
  updated_at?: string;
}

// Add interface for the resources query response
interface ResourceQueryResponse {
  id: string;
  resource_id: string;
  resources: {
    id: string;
    name: string;
  };
}

// Add new interface for blocked time slots
interface BlockedTimeSlot {
  date: string;
  blockedSlots: string[];
}

interface BlockedTimeSlots {
  [resourceId: string]: BlockedTimeSlot[];
}

// Helper function to get days in month with proper day offset
const getDaysInMonthArray = (date: Date) => {
  // Create date objects for first and last day of month, using noon to avoid timezone issues
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1, 12, 0, 0);
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0, 12, 0, 0);
  const days = [];
  
  // Get the day of week (0-6, where 0 is Sunday)
  let startingDay = firstDay.getDay();
  // Convert to Monday-based week (0-6, where 0 is Monday)
  startingDay = startingDay === 0 ? 6 : startingDay - 1;
  
  // Add empty days for padding at start
  for (let i = 0; i < startingDay; i++) {
    days.push(null);
  }
  
  // Add actual days
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(i);
  }
  
  return days;
};

export default function CreateMobileBookingModal({
  isOpen,
  onClose,
  onSuccess,
  onSwitchToLocation
}: CreateMobileBookingModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [title, setTitle] = useState('Mobile Booking');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedServices, setSelectedServices] = useState<Array<{
    vehicle: CustomerVehicle;
    service: ExtendedService;
    priceOption: any;
  }>>([]);
  const [currentVehicle, setCurrentVehicle] = useState<CustomerVehicle | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [selectedServiceToEdit, setSelectedServiceToEdit] = useState<any>(null);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [selectedVehicleToEdit, setSelectedVehicleToEdit] = useState<CustomerVehicle | null>(null);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    dropOffDate: '',
    dropOffTime: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    customer_notes: ''
  });
  const [bookingColor, setBookingColor] = useState('#3b82f6'); // Default blue
  const [bookingSecondaryColor, setBookingSecondaryColor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSelectAddonsModalOpen, setIsSelectAddonsModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState<ExtendedService | null>(null);
  const [selectedServiceAddons, setSelectedServiceAddons] = useState<Record<string, ServiceAddon[]>>({});
  const [mobileAvailability, setMobileAvailability] = useState<MobileAvailability[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [postalCode, setPostalCode] = useState<string>('');
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isResourceSelectorOpen, setIsResourceSelectorOpen] = useState(false);
  const [availableResources, setAvailableResources] = useState<Resource[]>([]);
  const [resourceNames, setResourceNames] = useState<Record<string, string>>({});
  const [bookingAddress, setBookingAddress] = useState<string>('');
  const [blockedTimeSlots, setBlockedTimeSlots] = useState<BlockedTimeSlots>({});
  const [blockedSlotsLoading, setBlockedSlotsLoading] = useState(false);
  const [blockedSlotsError, setBlockedSlotsError] = useState<string | null>(null);
  const [bufferTime, setBufferTime] = useState<number>(30); // Default buffer time (30 minutes)
  const [bufferTimeLoading, setBufferTimeLoading] = useState(false);

  // ✨ EVENT-DRIVEN PERSISTENCE - Clean, predictable saves on user actions only!
  const { saveModalData, loadModalData, clearModalData } = useEventDrivenModalPersistence('create_mobile_booking');

  // Helper function to save current form state with optional overrides
  const saveMobileBookingProgress = (overrides: Record<string, any> = {}) => {
    const formData = {
      title,
      selectedCustomer,
      selectedServices,
      scheduleData,
      bookingColor,
      bookingSecondaryColor,
      selectedServiceAddons,
      bookingAddress,
      postalCode,
      selectedResource,
      ...overrides // Apply any immediate overrides to handle state timing issues
    };
    console.log('💾 Saving CreateMobileBookingModal progress:', Object.keys(formData));
    saveModalData(formData);
  };

  // Load saved data when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📥 Loading CreateMobileBookingModal progress...');
      const savedData = loadModalData();
      
      if (Object.keys(savedData).length > 0) {
        console.log('📥 Restoring saved CreateMobileBookingModal progress:', {
          keys: Object.keys(savedData),
          customerName: savedData.selectedCustomer ? `${savedData.selectedCustomer.first_name} ${savedData.selectedCustomer.last_name}` : 'none',
          servicesCount: savedData.selectedServices?.length || 0,
          hasSchedule: !!savedData.scheduleData?.startDate,
          hasResource: !!savedData.selectedResource
        });
        
        if (savedData.title) setTitle(savedData.title);
        if (savedData.selectedCustomer) {
          console.log('📥 Restoring customer:', savedData.selectedCustomer);
          setSelectedCustomer(savedData.selectedCustomer);
        }
        if (savedData.selectedServices) setSelectedServices(savedData.selectedServices);
        if (savedData.scheduleData) setScheduleData(savedData.scheduleData);
        if (savedData.bookingColor) setBookingColor(savedData.bookingColor);
        if (savedData.bookingSecondaryColor !== undefined) setBookingSecondaryColor(savedData.bookingSecondaryColor);
        if (savedData.selectedServiceAddons) setSelectedServiceAddons(savedData.selectedServiceAddons);
        if (savedData.bookingAddress) setBookingAddress(savedData.bookingAddress);
        if (savedData.postalCode) setPostalCode(savedData.postalCode);
        if (savedData.selectedResource) setSelectedResource(savedData.selectedResource);
        
        console.log('📥 CreateMobileBookingModal progress restored successfully');
      } else {
        console.log('📥 No saved CreateMobileBookingModal progress found');
      }
    }
  }, [isOpen]);

  // Calculate total duration in minutes
  const totalDurationMinutes = selectedServices.reduce((total, service) => {
    // Add service duration
    let duration = total + service.priceOption.duration_minutes;
    // Add addon durations for this service
    const addons = selectedServiceAddons[service.service.id] || [];
    duration += addons.reduce((addonSum, addon) => addonSum + addon.duration_minutes, 0);
    return duration;
  }, 0);

  // Add effect to reset schedule when duration changes
  useEffect(() => {
    if (scheduleData.startDate || scheduleData.startTime) {
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
    }
  }, [totalDurationMinutes]);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Add effect to set initial booking address when customer is selected
  useEffect(() => {
    if (selectedCustomer?.address) {
      console.log('Setting booking address from customer:', selectedCustomer.address);
      setBookingAddress(selectedCustomer.address);
    } else {
      console.log('No customer address available');
      setBookingAddress('');
    }
  }, [selectedCustomer]);

  // Add effect to extract postal code from booking address
  useEffect(() => {
    if (bookingAddress) {
      // Regular expression to match a Canadian postal code (e.g., V3R 1A2 or V3R1A2)
      const postalCodeRegex = /[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d/i;
      const match = bookingAddress.match(postalCodeRegex);
      
      if (match) {
        const extractedPostalCode = match[0].replace(/\s+/g, '').toUpperCase();
        console.log('Extracted postal code from address:', extractedPostalCode, 'Using FSA:', extractedPostalCode.substring(0, 3));
        
        // Only update if postal code is empty or different to avoid unnecessary state changes
        if (!postalCode || postalCode !== extractedPostalCode.substring(0, 3)) {
          // Set only the first 3 characters of the postal code (FSA)
          setPostalCode(extractedPostalCode.substring(0, 3));
        }
      } else if (bookingAddress && !postalCode) {
        console.log('No postal code detected in address:', bookingAddress);
      }
    }
  }, [bookingAddress]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      clearModalData(); // Clear saved state when modal closes
      setTitle('Mobile Booking');
      setSelectedCustomer(null);
      setCurrentVehicle(null);
      setSelectedServices([]);
      setBookingAddress('');
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
      setError(null);
    }
  };

  const handleVehicleCreated = async (vehicle: CustomerVehicle) => {
    // Update customer data to get the new vehicle
    const updatedCustomer = await getCustomer(selectedCustomer?.id || '');
    setSelectedCustomer(updatedCustomer);
    
    // Set the new vehicle as current and open service selection
    setCurrentVehicle(vehicle);
    setIsCreateVehicleModalOpen(false);
    setIsSelectServiceModalOpen(true);
    
    // Save progress after vehicle creation
    saveMobileBookingProgress();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Validation checks...
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      if (!selectedCustomer) {
        throw new Error('Please select a customer');
      }

      if (!selectedBusiness) {
        throw new Error('Please select a business');
      }

      if (selectedServices.length === 0) {
        throw new Error('Please select at least one service');
      }

      if (!selectedResource) {
        throw new Error('Please select a resource for this booking');
      }

      if (!selectedResource?.resource_id) {
        throw new Error('Invalid resource selected');
      }

      if (!scheduleData?.startDate || !scheduleData?.startTime || !scheduleData?.endDate || !scheduleData?.endTime) {
        throw new Error('Please select a date and time for the booking');
      }
      
      // Log the buffer time being used
      console.log(`Creating mobile booking with buffer time: ${bufferTime} minutes`);

      // Create booking params object
      const bookingParams = {
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        resource_id: selectedResource.resource_id,
        title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
        start_date: scheduleData.startDate,
        start_time: scheduleData.startTime,
        end_date: scheduleData.endDate,
        end_time: scheduleData.endTime,
        buffer_time: bufferTime,
        status: 'scheduled',
        notes: `Created with ${selectedServices.length} services`,
        customer_notes: scheduleData.customer_notes || "",
        color: bookingColor,
        secondary_color: bookingSecondaryColor || undefined,
        booking_address: bookingAddress,
        vehicles: Object.entries(servicesByVehicle).map(([vehicleId, services]) => ({
          vehicle_id: vehicleId,
          services: services.map(service => {
            const addons = selectedServiceAddons[service.service.id] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : 5;
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            
            return {
              service_id: service.service.id,
              name: service.service.name,
              description: service.service.description || '',
              price: servicePrice,
              tax_exempt: service.service.tax_exempt || false,
              tax_rate: taxRate,
              tax_amount: taxAmount,
              total_amount: taxableAmount + taxAmount,
              duration_minutes: service.priceOption.duration_minutes
            };
          })
        }))
      };
      
      console.log('Creating mobile booking with params:', {
        ...bookingParams,
        buffer_time: bookingParams.buffer_time // Explicitly log the buffer time
      });

      // Create the booking
      const result = await createMobileBooking(bookingParams);

      if (!result || !result.id) throw new Error('No booking data returned');

      // Insert add-ons for each service
      for (const serviceData of selectedServices) {
        const addons = selectedServiceAddons[serviceData.service.id] || [];
        if (addons.length > 0) {
          const { data: serviceResult } = await supabase
            .from('booking_services')
            .select('id')
            .eq('booking_id', result.id)
            .eq('service_id', serviceData.service.id)
            .single();

          if (serviceResult) {
            const { error: addonsError } = await supabase
              .from('booking_service_addons')
              .insert(
                addons.map(addon => ({
                  booking_service_id: serviceResult.id,
                  addon_id: addon.id,
                  price: addon.price
                }))
              );

            if (addonsError) throw addonsError;
          }
        }
      }

      // Reset all state before closing
      setTitle('Mobile Booking');
      setSelectedCustomer(null);
      setCurrentVehicle(null);
      setSelectedServices([]);
      setSelectedServiceAddons({});
      setBookingAddress('');
      setPostalCode('');
      setSelectedResource(null);
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
      setError(null);

      // Clear saved modal state
      localStorage.removeItem('gitbooked_create_mobile_booking_simple');

      // Trigger automations for mobile booking creation
      try {
        console.log('🤖 MOBILE BOOKING: Triggering booking automations for created event...');
        
        // Build comprehensive booking data for automation (same structure as CreateBookingModal)
        const realBookingData = {
          booking_id: result.booking_number || `#${result.id.slice(-4)}`, // Use booking number like "#1134"
          title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
          grand_total: selectedServices.reduce((total, service) => {
            const addons = selectedServiceAddons[service.service.id] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : (service.service.tax_rate || 5);
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            return total + taxableAmount + taxAmount;
          }, 0),
          
          // Customer information
          customer: {
            id: selectedCustomer.id,
            name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            full_name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            first_name: selectedCustomer.first_name,
            last_name: selectedCustomer.last_name,
            email: selectedCustomer.email,
            phone: selectedCustomer.phone
          },
          
          // Schedule information
          schedule: {
            start_date: scheduleData.startDate,
            start_time: scheduleData.startTime,
            end_date: scheduleData.endDate,
            end_time: scheduleData.endTime,
            drop_off_date: scheduleData.dropOffDate,
            drop_off_time: scheduleData.dropOffTime
          },
          
          // Services information
          services: selectedServices.map(service => ({
            id: service.service.id,
            name: service.service.name,
            price: service.priceOption.price,
            duration_minutes: service.priceOption.duration_minutes,
            vehicle: {
              id: service.vehicle.id,
              make: service.vehicle.make,
              model: service.vehicle.model,
              year: service.vehicle.year,
              color: service.vehicle.color
            },
            addons: selectedServiceAddons[service.service.id] || []
          })),
          
          // Resource assigned (mobile booking has a single resource)
          resources: selectedResource ? [{
            id: selectedResource.resource_id,
            name: resourceNames[selectedResource.resource_id] || 'Mobile Technician',
            phone: '', // Mobile resource phone not available in this context
            role: 'mobile_technician'
          }] : [],
          
          // Vehicle summary for easy access
          vehicles: (() => {
            const vehicles = selectedServices.map(service => ({
              make: service.vehicle.make || '',
              model: service.vehicle.model || '',
              year: service.vehicle.year || 0,
              color: service.vehicle.color || '',
              display: `${service.vehicle.year || ''} ${service.vehicle.make || ''} ${service.vehicle.model || ''}`.trim()
            }));
            
            // Remove duplicates based on make/model/year combination
            const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
              index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
            );
            
            return uniqueVehicles;
          })(),
          
          // Additional booking details
          notes: scheduleData.customer_notes || null,
          booking_address: bookingAddress || null,
          color: bookingColor,
          secondary_color: bookingSecondaryColor || null,
          created_at: new Date().toISOString(),
          status: 'scheduled',
          mobile_booking: true
        };

        console.log('📋 Mobile booking data:', realBookingData);
        
        // Trigger automation (non-blocking)
        triggerBookingAutomations(selectedBusiness.id, 'created', realBookingData)
          .then(() => console.log('🤖 MOBILE BOOKING: Booking automations completed successfully'))
          .catch(automationError => console.error('❌ MOBILE BOOKING: Error triggering booking automations:', automationError));
          
        console.log('🤖 MOBILE BOOKING: Booking automation trigger initiated');
      } catch (automationError) {
        console.error('❌ MOBILE BOOKING: Failed to trigger booking automations:', automationError);
        // We don't throw here to avoid blocking the booking creation flow
      }

      // Clear the modal persistence data on successful booking creation
      clearModalData();
      console.log('🗑️ Mobile booking created successfully - cleared saved progress');

      // Call success callback and close modal
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error creating booking:', error);
      setError(error instanceof Error ? error.message : 'Failed to create booking. Please check all required fields.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = (service: ExtendedService, priceOption: any) => {
    if (!currentVehicle) return;

    const newService = {
      vehicle: currentVehicle,
      service,
      priceOption
    };

    const newSelectedServices = [...selectedServices, newService];
    console.log('📱 Service added in CreateMobileBookingModal:', service.name);
    setSelectedServices(newSelectedServices);
    
    // Save progress immediately with the new services array
    saveMobileBookingProgress({ selectedServices: newSelectedServices });
    
    // After adding the service, show the add-ons modal
    setCurrentService(service);
    setIsSelectAddonsModalOpen(true);
  };

  const handleAddAddons = (addons: ServiceAddon[]) => {
    if (!currentService) return;
    
    const newAddons = {
      ...selectedServiceAddons,
      [currentService.id]: addons
    };
    
    console.log('📱 Add-ons added in CreateMobileBookingModal for service:', currentService.name);
    setSelectedServiceAddons(newAddons);
    
    // Save progress immediately with the new addons
    saveMobileBookingProgress({ selectedServiceAddons: newAddons });
  };

  // Group services by vehicle
  const servicesByVehicle = selectedServices.reduce<Record<string, typeof selectedServices>>((acc, service) => {
    const vehicleId = service.vehicle.id;
    if (!acc[vehicleId]) {
      acc[vehicleId] = [];
    }
    acc[vehicleId].push(service);
    return acc;
  }, {});

  const handleSelectVehicle = (vehicle: CustomerVehicle) => {
    // If we already have services for this vehicle, keep using it
    const existingVehicleServices = selectedServices.find(s => s.vehicle.id === vehicle.id);
    const vehicleToSet = existingVehicleServices ? existingVehicleServices.vehicle : vehicle;
    
    console.log('📱 Vehicle selected in CreateMobileBookingModal:', vehicleToSet);
    setCurrentVehicle(vehicleToSet);
    setIsSelectServiceModalOpen(true);
    setIsSelectVehicleModalOpen(false);
    
    // Save progress immediately with the new vehicle data
    saveMobileBookingProgress({ currentVehicle: vehicleToSet });
  };

  const handleRemoveService = (vehicleId: string, serviceIndex: number) => {
    setSelectedServices(prev => prev.filter((s, idx) => !(s.vehicle.id === vehicleId && idx === serviceIndex)));
    
    // Save progress after removing service
    setTimeout(() => saveMobileBookingProgress(), 100);
  };

  const handleRemoveAddon = (serviceId: string, addonId: string) => {
    setSelectedServiceAddons(prev => ({
      ...prev,
      [serviceId]: prev[serviceId]?.filter(addon => addon.id !== addonId) || []
    }));
    
    // Save progress after removing addon
    setTimeout(() => saveMobileBookingProgress(), 100);
  };

  // Fetch mobile availability data
  const fetchMobileAvailability = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      setAvailabilityLoading(true);
      setAvailabilityError(null);

      const { data, error } = await supabase
        .from('mobile_availability')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('mobile_available', true);

      if (error) throw error;

      console.log('Fetched mobile availability:', data);
      setMobileAvailability(data || []);
    } catch (err) {
      console.error('Error fetching mobile availability:', err);
      setAvailabilityError(err instanceof Error ? err.message : 'Failed to fetch mobile availability');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  // Fetch buffer time from online_booking_settings
  const fetchBufferTime = async () => {
    if (!supabase || !selectedBusiness?.id) return;
    
    try {
      setBufferTimeLoading(true);
      
      const { data, error } = await supabase
        .from('online_booking_settings')
        .select('buffer_time')
        .eq('business_id', selectedBusiness.id)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          // Record not found, keeping default value
          console.log('No buffer time setting found, using default');
        } else {
          console.error('Error fetching buffer time:', error);
        }
        return;
      }
      
      if (data?.buffer_time !== undefined && data?.buffer_time !== null) {
        console.log(`Setting buffer time to ${data.buffer_time} minutes from database`);
        setBufferTime(data.buffer_time);
      }
    } catch (error) {
      console.error('Error fetching buffer time:', error);
    } finally {
      setBufferTimeLoading(false);
    }
  };

  // Fetch availability data when modal opens
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      fetchMobileAvailability();
      fetchBufferTime();
    }
  }, [isOpen, selectedBusiness?.id]);

  // Function to fetch blocked time slots
  const fetchBlockedTimeSlots = async () => {
    if (!supabase) {
      console.error('Supabase client is not initialized');
      setBlockedSlotsError('Database connection not available');
      setBlockedSlotsLoading(false);
      return;
    }

    try {
      setBlockedSlotsLoading(true);
      setBlockedSlotsError(null);

      // Get current date at start of day in local timezone
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const localDateStr = format(now, 'yyyy-MM-dd');

      // Fetch all future mobile bookings for this business, including buffer blocks
      const { data: mobileBookings, error } = await supabase
        .from('mobile_bookings')
        .select('*')
        .eq('business_id', selectedBusiness?.id)
        .gte('start_date', localDateStr)
        .order('start_date', { ascending: true });

      if (error) throw error;

      console.log('Fetched mobile bookings for blocked slots:', mobileBookings);

      // Process the bookings into blocked time slots
      const blocked: BlockedTimeSlots = {};

      mobileBookings.forEach(booking => {
        const { resource_id, start_date, start_time, end_time, is_buffer_block } = booking;

        // Log buffer blocks for debugging
        if (is_buffer_block) {
          console.log('Processing buffer block:', {
            resource_id,
            start_date,
            start_time,
            end_time
          });
        }

        // Initialize array for this resource if it doesn't exist
        if (!blocked[resource_id]) {
          blocked[resource_id] = [];
        }

        // Find or create the entry for this date
        let dateEntry = blocked[resource_id].find(entry => entry.date === start_date);
        if (!dateEntry) {
          dateEntry = { date: start_date, blockedSlots: [] };
          blocked[resource_id].push(dateEntry);
        }

        // Calculate all 30-minute slots between start_time and end_time
        const [startHour, startMinute] = start_time.split(':').map(Number);
        const [endHour, endMinute] = end_time.split(':').map(Number);
        const startMinutes = startHour * 60 + startMinute;
        const endMinutes = endHour * 60 + endMinute;

        // Generate all 30-minute slots that are blocked
        for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
          const hour = Math.floor(minutes / 60);
          const minute = minutes % 60;
          const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          if (!dateEntry.blockedSlots.includes(timeSlot)) {
            dateEntry.blockedSlots.push(timeSlot);
          }
        }
      });

      console.log('Blocked time slots:', blocked);
      setBlockedTimeSlots(blocked);
    } catch (error) {
      console.error('Error fetching blocked time slots:', error);
      setBlockedSlotsError(error instanceof Error ? error.message : 'Error fetching blocked time slots');
    } finally {
      setBlockedSlotsLoading(false);
    }
  };

  // Fetch blocked time slots when modal opens or business changes
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      fetchBlockedTimeSlots();
    }
  }, [isOpen, selectedBusiness?.id]);

  // Update the isTimeSlotAvailable function to be more efficient
  const isTimeSlotAvailable = (date: Date, timeString: string) => {
    // If we're still loading blocked slots, return false
    if (blockedSlotsLoading) return false;

    // Get available resources for this time slot
    const availableResources = getAvailableResourcesForTimeSlot(date, timeString);
    
    // If there are no available resources, the time slot is not available
    return availableResources.length > 0;
  };

  // Update the isDayAvailable function to remove verbose logging
  const isDayAvailable = (date: Date) => {
    const localDate = new Date(date);
    const offset = localDate.getTimezoneOffset();
    localDate.setMinutes(localDate.getMinutes() + offset);
    const dayOfWeek = format(localDate, 'EEEE').toLowerCase();
    
    return mobileAvailability.some(resource => {
      try {
        // Only check postal code availability if a postal code is provided
        if (postalCode && postalCode.trim() !== '') {
          let postalCodes = resource.postal_codes;
          if (typeof postalCodes === 'string') {
            try {
              postalCodes = JSON.parse(postalCodes);
            } catch (err) {
              return false;
            }
          }
          
          const availablePostalCodes = postalCodes?.[dayOfWeek] || [];
          const enteredPostalCode = postalCode.toUpperCase().replace(/\s+/g, '');
          
          if (!availablePostalCodes.includes(enteredPostalCode)) {
            return false;
          }
        }
        
        // Check working hours
        let workingHours = resource.working_hours;
        if (typeof workingHours === 'string') {
          try {
            workingHours = JSON.parse(workingHours);
          } catch (err) {
            return false;
          }
        }
        
        const daySchedule = workingHours?.[dayOfWeek];
        if (!daySchedule?.enabled || !daySchedule?.shifts?.length) {
          return false;
        }

        return true;
      } catch (err) {
        console.error('Error checking day availability:', err);
        return false;
      }
    });
  };

  // Update the hasTimePassed function to remove verbose logging
  const hasTimePassed = (date: Date | string, timeString: string): boolean => {
    const now = new Date();
    const [hours, minutes] = timeString.split(':').map(Number);
    const checkDate = typeof date === 'string' ? new Date(date + 'T12:00:00Z') : new Date(date);

    // Compare dates in UTC to avoid timezone issues
    const nowUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const checkUTC = Date.UTC(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());

    // For future dates, only check if the time is within business hours (9am-5pm)
    if (checkUTC > nowUTC) {
      const slotMinutes = hours * 60 + minutes;
      return slotMinutes < (9 * 60) || slotMinutes > (17 * 60);
    }

    // For today, check if the time has passed
    if (checkUTC === nowUTC) {
      const slotMinutes = hours * 60 + minutes;
      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
      return currentTimeMinutes >= slotMinutes;
    }

    // Past dates are always considered passed
    return true;
  };

  // Update the handleDateSelect function with better logging
  const handleDateSelect = (day: number) => {
    try {
      // Create date at noon UTC to avoid any timezone issues
      const selectedDate = new Date(Date.UTC(
        currentMonth.getUTCFullYear(),
        currentMonth.getUTCMonth(),
        day,
        12, // Set to noon UTC
        0,
        0
      ));
      
      // Format date as YYYY-MM-DD using UTC date
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      
      console.log('Setting new date:', {
        selectedDate: selectedDate.toISOString(),
        formattedDate,
        day,
        currentMonth: currentMonth.toISOString()
      });

      // Reset all time-related fields when selecting a new date
      setScheduleData(prev => ({
        ...prev,
        dropOffDate: formattedDate,
        startDate: formattedDate,
        endDate: formattedDate,
        // Reset time fields
        dropOffTime: '',
        startTime: '',
        endTime: ''
      }));

      // Also reset the selected resource since it's tied to a specific time slot
      setSelectedResource(null);
    } catch (err) {
      console.error('Error selecting date:', err);
      setError('Failed to select date. Please try again.');
    }
  };

  // Update the renderCalendar function to check postal code for day availability
  const renderCalendar = () => {
    const days = getDaysInMonthArray(currentMonth);
    const dayHeaders = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const today = new Date();

    return (
      <div className="mt-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-900">Schedule</h3>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="p-1.5 hover:bg-gray-100 rounded-full"
            >
              <ChevronDown className="w-4 h-4 transform rotate-90" />
            </button>
            <span className="text-sm font-medium">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="p-1.5 hover:bg-gray-100 rounded-full"
            >
              <ChevronDown className="w-4 h-4 transform -rotate-90" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayHeaders.map((day) => (
            <div key={day} className="text-center text-sm font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            if (!day) return <div key={index} className="aspect-square" />;
            
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const dateStr = format(date, 'yyyy-MM-dd');
            const isSelected = dateStr === scheduleData.startDate;
            const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const hasAvailability = isDayAvailable(date);

            return (
              <button
                key={index}
                type="button"
                disabled={isPast || !hasAvailability}
                onClick={() => !isPast && hasAvailability && handleDateSelect(day)}
                className={`
                  aspect-square p-2 rounded-lg text-center relative
                  ${isPast || !hasAvailability ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-100'}
                  ${isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'text-gray-900'}
                  ${date.getDate() === today.getDate() && 
                    date.getMonth() === today.getMonth() && 
                    date.getFullYear() === today.getFullYear() 
                    ? 'font-bold' 
                    : ''}
                `}
              >
                {day}
                {hasAvailability && !isPast && !isSelected && (
                  <div 
                    className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-blue-500"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Time slots */}
        {scheduleData.startDate && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Available Times</h4>
            <div className="grid grid-cols-4 gap-2">
              {generateTimeSlots().map((time) => {
                const date = new Date(scheduleData.startDate);
                const availableResources = getAvailableResourcesForTimeSlot(date, time);
                const isAvailable = availableResources.length > 0;
                const isSelected = scheduleData.startTime === time;
                
                return (
                  <button
                    key={time}
                    type="button"
                    disabled={!isAvailable}
                    onClick={() => {
                      if (isAvailable) {
                        handleTimeSlotSelect(time);
                      }
                    }}
                    className={`
                      px-2 py-1.5 text-sm rounded-md text-center
                      ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'}
                      ${!isAvailable ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}
                      ${isSelected && isAvailable ? 'hover:bg-blue-700' : ''}
                    `}
                  >
                    {format(new Date(`2000-01-01T${time}`), 'h:mm a')}
                    {isAvailable && availableResources.length > 1 && (
                      <div className="text-xs mt-0.5 text-gray-500">
                        {availableResources.length} available
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Update the fetchResourceNames function to directly query resources table
  const fetchResourceNames = async (resourceIds: string[]) => {
    if (!supabase || !resourceIds.length) return;

    try {
      // Get the resource_ids from mobile_availability records
      const resourceIdsToFetch = mobileAvailability
        .filter(ma => resourceIds.includes(ma.id))
        .map(ma => ma.resource_id);

      console.log('Fetching names for resource_ids:', resourceIdsToFetch);
      
      const { data, error } = await supabase
        .from('resources')
        .select('id, first_name, last_name')
        .in('id', resourceIdsToFetch);

      if (error) throw error;

      console.log('Fetched resource data:', data);
      
      // Map the resource names back to mobile_availability ids
      const names = mobileAvailability.reduce((acc, ma) => {
        const resource = data?.find(r => r.id === ma.resource_id);
        if (resource) {
          console.log(`Found name for resource ${ma.resource_id}:`, `${resource.first_name} ${resource.last_name}`);
          return {
            ...acc,
            [ma.id]: `${resource.first_name} ${resource.last_name}`
          };
        }
        return {
          ...acc,
          [ma.id]: `Resource ${ma.resource_id}`
        };
      }, {} as Record<string, string>);

      console.log('Setting resource names:', names);
      setResourceNames(names);
    } catch (err) {
      console.error('Error fetching resource names:', err);
    }
  };

  // Update useEffect to fetch resource names when mobileAvailability changes
  useEffect(() => {
    if (mobileAvailability.length > 0) {
      const resourceIds = mobileAvailability.map(r => r.id);
      fetchResourceNames(resourceIds);
    }
  }, [mobileAvailability]);

  // Update the handleTimeSlotSelect function to separate buffer time from displayed end time
  const handleTimeSlotSelect = (time: string) => {
    console.log('Time slot selected:', {
      time,
      currentScheduleData: scheduleData,
      hasStartDate: !!scheduleData.startDate,
      serviceDuration: totalDurationMinutes,
      bufferTime,
      totalWithBuffer: totalDurationMinutes + bufferTime
    });

    if (!scheduleData.startDate) {
      console.error('No start date set when selecting time slot');
      return;
    }

    const date = new Date(scheduleData.startDate);
    const resources = getAvailableResourcesForTimeSlot(date, time);
    
    console.log('Available resources for time slot:', {
      time,
      date: date.toISOString(),
      resourceCount: resources.length,
      resources: resources.map(r => r.id)
    });

    if (resources.length === 0) {
      console.error('No available resources for selected time slot');
      return;
    }

    // Create start date/time
    const [hours, minutes] = time.split(':').map(Number);
    const startDateTime = new Date(scheduleData.startDate + 'T' + time);
    
    // Calculate end date/time by adding only the service duration (not buffer time)
    // Buffer time is used for availability but is not shown to the customer
    const endDateTime = new Date(startDateTime.getTime() + totalDurationMinutes * 60000);
    
    // Format end date and time separately
    const endDate = format(endDateTime, 'yyyy-MM-dd');
    const endTime = format(endDateTime, 'HH:mm');

    console.log('Setting schedule data:', {
      startDateTime: startDateTime.toISOString(),
      endDateTime: endDateTime.toISOString(),
      startDate: scheduleData.startDate,
      startTime: time,
      endDate,
      endTime,
      serviceDuration: totalDurationMinutes,
      bufferTime,
      totalWithBuffer: totalDurationMinutes + bufferTime
    });

    if (resources.length === 1) {
      // Auto-select the only available resource
      console.log('📱 Auto-selecting single available resource in CreateMobileBookingModal:', {
        resource: resources[0],
        resourceId: resources[0].resource_id,
        resourceName: resourceNames[resources[0].id]
      });
      
      const newScheduleData = {
        ...scheduleData,
        dropOffTime: time,
        startTime: time,
        endDate: endDate,
        endTime: endTime
      };
      
      setSelectedResource(resources[0]);
      setScheduleData(newScheduleData);
      
      // Save progress immediately with both schedule and resource data
      saveMobileBookingProgress({ 
        scheduleData: newScheduleData,
        selectedResource: resources[0]
      });
    } else {
      // Show resource selector for multiple available resources
      console.log('📱 Multiple resources available in CreateMobileBookingModal:', {
        count: resources.length,
        resources: resources.map(r => ({
          id: r.id,
          resourceId: r.resource_id,
          name: resourceNames[r.id]
        }))
      });
      
      const newScheduleData = {
        ...scheduleData,
        dropOffTime: time,
        startTime: time,
        endDate: endDate,
        endTime: endTime
      };
      
      setAvailableResources(resources);
      setIsResourceSelectorOpen(true);
      setScheduleData(newScheduleData);
      
      // Save progress immediately with schedule data
      saveMobileBookingProgress({ scheduleData: newScheduleData });
    }
  };

  // Add ResourceSelector modal component
  const ResourceSelector = () => {
    if (!isResourceSelectorOpen) return null;

    const hasUnloadedNames = availableResources.some(r => !resourceNames[r.id]);

    const handleResourceSelect = (resource: Resource) => {
      console.log('📱 Resource selected in CreateMobileBookingModal:', {
        resource,
        resourceId: resource.resource_id,
        resourceName: resourceNames[resource.id]
      });
      setSelectedResource(resource);
      setIsResourceSelectorOpen(false);
      
      // Save progress immediately with the new resource
      saveMobileBookingProgress({ selectedResource: resource });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Select Resource</h3>
          <div className="space-y-2">
            {hasUnloadedNames ? (
              <div className="text-sm text-gray-600">Loading resources...</div>
            ) : (
              availableResources.map(resource => (
                <button
                  key={resource.id}
                  onClick={() => handleResourceSelect(resource)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  {resourceNames[resource.id] || resource.name}
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => setIsResourceSelectorOpen(false)}
            className="mt-4 w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Update the payment summary section to show end time with buffer
  const renderPaymentSummary = () => (
    <div className="mt-2">
      <h3 className="text-sm font-medium text-gray-900 mb-2">Payment Summary</h3>
      
      {scheduleData?.startDate && scheduleData?.startTime && (
        <div className="mb-3 space-y-0.5 pb-3 border-b border-gray-100">
          <div className="text-sm text-gray-600">
            Date: {format(new Date(scheduleData.startDate + 'T12:00:00Z'), 'EEEE, MMMM d, yyyy')}
          </div>
          <div className="text-sm text-gray-600">
            Start Time: {format(new Date(`2000-01-01T${scheduleData.startTime}`), 'h:mm a')}
          </div>
          <div className="text-sm text-gray-600">
            End Time: {format(new Date(`2000-01-01T${scheduleData.startTime}`).getTime() + totalDurationMinutes * 60000, 'h:mm a')}
          </div>
          <div className="text-sm text-gray-600 text-blue-600">
            End Time with Buffer: {format(new Date(`2000-01-01T${scheduleData.startTime}`).getTime() + (totalDurationMinutes + bufferTime) * 60000, 'h:mm a')}
          </div>
          {selectedResource && (
            <div className="text-sm text-gray-600">
              Resource: {resourceNames[selectedResource.id] || selectedResource.name}
            </div>
          )}
        </div>
      )}

      <div className="space-y-1 mb-3 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock className="h-4 w-4" />
          <span>Service Duration</span>
          <span className="ml-auto">
            {totalDurationMinutes >= 1440 
              ? `${Math.floor(totalDurationMinutes / 1440)} days` 
              : totalDurationMinutes >= 60
                ? `${Math.floor(totalDurationMinutes / 60)} hours ${totalDurationMinutes % 60 > 0 ? `${totalDurationMinutes % 60} mins` : ''}`
                : `${totalDurationMinutes} mins`}
          </span>
        </div>
        
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock className="h-4 w-4 text-blue-500" />
          <span>Total Duration with Buffer Time</span>
          <span className="ml-auto font-medium">
            {(totalDurationMinutes + bufferTime) >= 1440 
              ? `${Math.floor((totalDurationMinutes + bufferTime) / 1440)} days` 
              : (totalDurationMinutes + bufferTime) >= 60
                ? `${Math.floor((totalDurationMinutes + bufferTime) / 60)} hours ${(totalDurationMinutes + bufferTime) % 60 > 0 ? `${(totalDurationMinutes + bufferTime) % 60} mins` : ''}`
                : `${totalDurationMinutes + bufferTime} mins`}
          </span>
        </div>
      </div>

      <div className="space-y-1">
        {/* Service Breakdown - Single line per service with add-ons */}
        {selectedServices.map((item) => {
          const addons = selectedServiceAddons[item.service.id] || [];
          return (
            <div key={`${item.service.id}-${item.vehicle.id}`} className="text-sm text-gray-500">
              {item.service.name} {addons.length > 0 ? `(+ ${addons.length} add-ons)` : ''}
            </div>
          );
        })}

        <div className="flex justify-between text-sm text-gray-600 pt-2">
          <span>Subtotal</span>
          <span>${(selectedServices.reduce((sum, s) => {
            let total = sum + s.priceOption.price;
            const addons = selectedServiceAddons[s.service.id] || [];
            total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
            return total;
          }, 0)).toLocaleString()}</span>
        </div>

        <div className="flex justify-between text-sm text-gray-600">
          <span>GST (5%)</span>
          <span>+ ${(selectedServices.reduce((sum, s) => {
            let total = s.priceOption.price;
            const addons = selectedServiceAddons[s.service.id] || [];
            total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
            return s.service.tax_exempt ? sum : sum + (total * 0.05);
          }, 0)).toLocaleString()}</span>
        </div>

        <div className="flex justify-between text-sm font-medium text-gray-900 pt-2 border-t border-gray-200 mt-2">
          <span>Total</span>
          <span>${(selectedServices.reduce((sum, s) => {
            let total = s.priceOption.price;
            const addons = selectedServiceAddons[s.service.id] || [];
            total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
            if (!s.service.tax_exempt) {
              total += total * 0.05;
            }
            return sum + total;
          }, 0)).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );

  // Update the getAvailableResourcesForTimeSlot function to include buffer time in availability calculations
  const getAvailableResourcesForTimeSlot = (date: Date, timeString: string): Resource[] => {
    // Convert date to local timezone string
    const localDate = new Date(date);
    const offset = localDate.getTimezoneOffset();
    localDate.setMinutes(localDate.getMinutes() + offset);
    const dateStr = format(localDate, 'yyyy-MM-dd');
    
    // If we're still loading blocked slots, return empty array
    if (blockedSlotsLoading) return [];

    // Calculate total duration including buffer time for the new booking
    const serviceDurationMinutes = selectedServices.reduce((total, service) => {
      let duration = total + (service.priceOption.duration_minutes || 0);
      const addons = selectedServiceAddons[service.service.id] || [];
      duration += addons.reduce((addonSum, addon) => addonSum + (addon.duration_minutes || 0), 0);
      return duration;
    }, 0);
    
    // Add buffer time to total duration for scheduling purposes
    const totalDurationWithBuffer = serviceDurationMinutes + bufferTime;

    // Convert booking start time to minutes since midnight
    const [startHour, startMinute] = timeString.split(':').map(Number);
    const bookingStartMinutes = startHour * 60 + startMinute;
    const bookingEndMinutes = bookingStartMinutes + totalDurationWithBuffer;

    console.log('Checking availability with buffer time:', {
      serviceDuration: serviceDurationMinutes,
      bufferTime,
      totalDurationWithBuffer,
      startTime: timeString,
      endMinutes: bookingEndMinutes
    });

    const availableResources = mobileAvailability.filter(resource => {
      try {
        // Check if any time during the booking duration (including buffer) overlaps with blocked slots
        const resourceBlockedSlots = blockedTimeSlots[resource.resource_id];
        if (resourceBlockedSlots) {
          const dateEntry = resourceBlockedSlots.find(entry => entry.date === dateStr);
          if (dateEntry) {
            // Convert blocked slots to minute ranges
            const blockedRanges = dateEntry.blockedSlots.map(slot => {
              const [hour, minute] = slot.split(':').map(Number);
              const slotMinutes = hour * 60 + minute;
              return {
                start: slotMinutes,
                end: slotMinutes + 30 // Each slot is 30 minutes
              };
            });

            // Check if booking overlaps with any blocked range
            const hasOverlap = blockedRanges.some(range => {
              // Booking starts before blocked slot ends AND booking ends after blocked slot starts
              return (bookingStartMinutes < range.end && bookingEndMinutes > range.start);
            });

            if (hasOverlap) {
              return false;
            }
          }
        }

        const dayOfWeek = format(localDate, 'EEEE').toLowerCase();

        // Only check postal code availability if a postal code is provided
        if (postalCode && postalCode.trim() !== '') {
          const postalCodes = resource.postal_codes?.[dayOfWeek] || [];
          const enteredPostalCode = postalCode.toUpperCase().replace(/\s+/g, '');
          if (!postalCodes.includes(enteredPostalCode)) {
            return false;
          }
        }

        // Check working hours
        let workingHours = resource.working_hours;
        if (typeof workingHours === 'string') {
          workingHours = JSON.parse(workingHours);
        }

        const daySchedule = workingHours?.[dayOfWeek];
        if (!daySchedule?.enabled || !daySchedule?.shifts?.length) {
          return false;
        }

        // Check if entire booking duration (including buffer) fits within any shift
        const isAvailable = daySchedule.shifts.some(shift => {
          const [shiftStartHour, shiftStartMinute] = shift.start.split(':').map(Number);
          const shiftStartMinutes = shiftStartHour * 60 + shiftStartMinute;

          const [shiftEndHour, shiftEndMinute] = shift.end.split(':').map(Number);
          const shiftEndMinutes = shiftEndHour * 60 + shiftEndMinute;
          
          return bookingStartMinutes >= shiftStartMinutes && bookingEndMinutes <= shiftEndMinutes;
        });

        return isAvailable;
      } catch (err) {
        console.error('Error checking resource availability:', err);
        return false;
      }
    }).map(resource => ({
      id: resource.id,                     // mobile_availability record ID
      resource_id: resource.resource_id,    // actual resource ID from resources table
      name: resourceNames[resource.id] || `Resource ${resource.resource_id}`,
      business_id: selectedBusiness?.id || '',
      mobile_available: true,
      working_hours: resource.working_hours,
      postal_codes: resource.postal_codes,
      created_at: resource.created_at,
      updated_at: resource.updated_at
    }));

    return availableResources;
  };

  // Update the generateTimeSlots function to handle timezone correctly
  const generateTimeSlots = (): string[] => {
    try {
      const slots: string[] = [];
      const startMinutes = 9 * 60; // 9 AM
      const endMinutes = 17 * 60; // 5 PM
      
      // Generate slots in 30-minute intervals
      for (let timeMinutes = startMinutes; timeMinutes <= endMinutes; timeMinutes += 30) {
        const hours = Math.floor(timeMinutes / 60);
        const minutes = timeMinutes % 60;
        const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        
        // Create a date object for the selected date at the current time slot
        const selectedDate = scheduleData.startDate 
          ? new Date(scheduleData.startDate + 'T12:00:00Z')
          : new Date();
        
        // Only add the slot if it hasn't passed (for today) or it's a future date
        if (!hasTimePassed(selectedDate, timeString)) {
          slots.push(timeString);
        }
      }

      console.log('Generated time slots:', slots);
      return slots;
    } catch (err) {
      console.error('Error generating time slots:', err);
      return [];
    }
  };

  // Update the location mode handler to use the new prop
  const handleLocationModeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    console.log("Switching from mobile to location booking");
    // If we have the onSwitchToLocation prop, use it
    if (onSwitchToLocation) {
      onSwitchToLocation();
    } else {
      // Otherwise, just close the modal (fallback to old behavior)
      onClose();
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
        } z-[9000]`}
        onClick={onClose}
      >
        <div 
          className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          } z-[9500]`}
          onTransitionEnd={handleTransitionEnd}
          onClick={e => e.stopPropagation()}
        >
          <div className="h-full flex flex-col">
            <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h2 className="text-lg font-semibold text-white">Mobile Booking</h2>
                
                {/* Location toggle button */}
                {onSwitchToLocation && (
                  <div 
                    className="flex bg-[#2f2f2f] rounded-lg p-1"
                    onClick={(e) => e.stopPropagation()} // Prevent clicks from propagating to parent
                  >
                    <button
                      type="button"
                      onClick={handleLocationModeClick}
                      className="px-3 py-1 text-sm rounded-md text-gray-400 hover:text-white"
                    >
                      Location
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1 text-sm rounded-md bg-blue-600 text-white"
                    >
                      Mobile
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form 
              onSubmit={handleSubmit} 
              className="flex-1 overflow-y-auto p-6"
            >
              <div className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm mb-4">
                    {error}
                  </div>
                )}

                {/* Title */}
                <div className="pb-4 border-b border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                    placeholder="Enter booking title"
                  />
                </div>

                {/* Customer Selection */}
                <div className="pb-4 border-b border-gray-200">
                  {selectedCustomer ? (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Customer
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsEditCustomerModalOpen(true)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                      </div>
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="text-sm font-medium text-gray-900">
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </div>
                        {selectedCustomer.email && (
                          <div className="text-sm text-gray-500 mt-1">
                            {selectedCustomer.email}
                          </div>
                        )}
                        {selectedCustomer.phone && (
                          <div className="text-sm text-gray-500 mt-1">
                            {selectedCustomer.phone}
                          </div>
                        )}
                        {selectedCustomer.address && (
                          <div className="text-sm text-gray-500 mt-1">
                            {selectedCustomer.address}
                          </div>
                        )}
                        {selectedCustomer.notes && (
                          <div className="text-sm text-gray-500 mt-1">
                            {selectedCustomer.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsSelectCustomerModalOpen(true)}
                      className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Select Customer
                    </button>
                  )}
                </div>

                {/* Booking Address */}
                {selectedCustomer && (
                  <div className="pb-4 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Booking Address
                      </label>
                      {bookingAddress && (
                        <button
                          type="button"
                          onClick={() => {
                            // Reset to customer's address
                            if (selectedCustomer?.address) {
                              setBookingAddress(selectedCustomer.address);
                              // Extract postal code from customer's address
                              const postalCodeRegex = /[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d/i;
                              const match = selectedCustomer.address.match(postalCodeRegex);
                              if (match) {
                                const fullPostalCode = match[0].replace(/\s+/g, '').toUpperCase();
                                setPostalCode(fullPostalCode.substring(0, 3)); // Use first 3 characters for area code
                                console.log(`Extracted postal code "${fullPostalCode}" from customer address, using FSA: ${fullPostalCode.substring(0, 3)}`);
                              } else {
                                console.log('No postal code found in customer address:', selectedCustomer.address);
                              }
                            }
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Reset to Customer Address
                        </button>
                      )}
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200">
                      <AddressAutocomplete
                        value={bookingAddress}
                        onChange={(value) => {
                          console.log('Address changed:', value);
                          setBookingAddress(value);
                        }}
                        onSelect={(address) => {
                          console.log('Address selected from Google Autocomplete:', address);
                          setBookingAddress(address.formatted);
                          if (address.postalCode) {
                            const cleanPostalCode = address.postalCode.replace(/\s+/g, '').toUpperCase();
                            console.log(`Setting postal code from Google Autocomplete: ${cleanPostalCode} (Full), ${cleanPostalCode.substring(0, 3)} (FSA)`);
                            setPostalCode(cleanPostalCode.substring(0, 3));
                          } else {
                            console.log('No postal code returned from Google Autocomplete');
                            // Try to extract from formatted address as fallback
                            const postalCodeRegex = /[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d/i;
                            const match = address.formatted.match(postalCodeRegex);
                            if (match) {
                              const extractedPostalCode = match[0].replace(/\s+/g, '').toUpperCase();
                              console.log(`Extracted postal code ${extractedPostalCode} from formatted address as fallback`);
                              setPostalCode(extractedPostalCode.substring(0, 3));
                            }
                          }
                        }}
                        placeholder="Enter service location address"
                        className="w-full h-10 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      />
                    </div>
                    {bookingAddress && (
                      <div className="text-xs text-gray-500 mt-1 flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {postalCode ? (
                          <span>Using postal code area: <span className="font-semibold text-blue-600">{postalCode}</span></span>
                        ) : (
                          <span className="text-amber-600">No postal code detected. Please enter one manually below.</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Services */}
                {selectedCustomer && (
                  <div className="pb-4 border-b border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Services
                    </label>
                    {/* Group services by vehicle */}
                    {Object.entries(servicesByVehicle).map(([vehicleId, services]) => {
                      const vehicle = services[0].vehicle; // Get vehicle info from first service
                      return (
                        <div key={vehicleId} className="mb-3">
                          <div className="bg-white rounded-lg border border-gray-200 p-4">
                            {/* Vehicle Header */}
                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
                              <div className="flex items-center">
                                <div className="text-sm font-medium text-gray-900">
                                  {vehicle.year} {vehicle.make} {vehicle.model}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedVehicleToEdit(vehicle);
                                    setIsEditVehicleModalOpen(true);
                                  }}
                                  className="ml-2 text-xs text-blue-600 hover:text-blue-800"
                                >
                                  Edit
                                </button>
                              </div>
                            </div>

                            {/* Services List */}
                            <div className="space-y-3">
                              {services.map((item, serviceIndex) => (
                                <div key={`${item.service.id}-${serviceIndex}`} className="pb-2 border-b border-gray-50">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <div className="text-sm text-gray-900">{item.service.name}</div>
                                        <div className="text-sm font-medium text-gray-900">${item.priceOption.price.toFixed(2)}</div>
                                      </div>
                                      <div className="mt-1 flex items-center text-xs text-gray-500">
                                        <Clock className="h-3.5 w-3.5 mr-1" />
                                        {item.priceOption.duration_minutes >= 1440 
                                          ? `${Math.round(item.priceOption.duration_minutes / 1440)} days`
                                          : `${Math.round(item.priceOption.duration_minutes / 60)} hours`
                                        }
                                      </div>
                                    </div>
                                    <div className="ml-4 flex items-center space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setCurrentService(item.service);
                                          setIsSelectAddonsModalOpen(true);
                                        }}
                                        className="text-xs text-green-600 hover:text-green-800"
                                      >
                                        Add-ons
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedServiceToEdit({
                                            vehicle,
                                            service: item.service,
                                            priceOption: item.priceOption,
                                            index: serviceIndex
                                          });
                                          setIsEditServiceModalOpen(true);
                                        }}
                                        className="text-xs text-blue-600 hover:text-blue-800"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveService(vehicle.id, serviceIndex)}
                                        className="text-xs text-red-600 hover:text-red-800"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                  {/* Service Add-ons */}
                                  {selectedServiceAddons[item.service.id]?.map(addon => (
                                    <div key={addon.id} className="mt-2 pl-4 border-l-2 border-gray-100">
                                      <div className="flex items-start justify-between">
                                        <div>
                                          <div className="text-sm text-gray-900">{addon.name}</div>
                                          <div className="text-xs text-gray-500">${addon.price.toFixed(2)}</div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Add Service Button */}
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedCustomer?.vehicles?.length) {
                          setIsCreateVehicleModalOpen(true);
                          return;
                        }
                        // If we have a current vehicle selected and it has services, keep using it
                        if (currentVehicle && selectedServices.some(s => s.vehicle.id === currentVehicle.id)) {
                          setIsSelectServiceModalOpen(true);
                        } else {
                          setIsSelectVehicleModalOpen(true);
                        }
                      }}
                      className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Service
                    </button>

                    {/* Add Another Vehicle Button */}
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedCustomer?.vehicles?.length) {
                          setIsCreateVehicleModalOpen(true);
                          return;
                        }
                        setCurrentVehicle(null);
                        setIsSelectVehicleModalOpen(true);
                      }}
                      className="w-full flex items-center justify-center px-4 py-3 mt-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400"
                    >
                      <Car className="h-4 w-4 mr-2" />
                      Add Another Vehicle
                    </button>
                  </div>
                )}

                {/* Customer Notes */}
                {selectedCustomer && (
                  <div className="pb-4 border-b border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Customer Notes</h3>
                    <textarea
                      value={scheduleData.customer_notes || ''}
                      onChange={(e) => setScheduleData(prev => ({
                        ...prev,
                        customer_notes: e.target.value
                      }))}
                      placeholder="Enter any notes about the customer or booking"
                      className="w-full h-24 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4 py-2"
                    />
                  </div>
                )}

                {/* Postal Code */}
                {selectedServices.length > 0 && bookingAddress && (
                  <div className="pb-4 border-b border-gray-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <h3 className="text-sm font-medium text-gray-700">Postal Code (First 3 Characters)</h3>
                      <div className="bg-blue-50 text-blue-800 px-2 py-0.5 text-xs rounded-full">
                        Required for scheduling
                      </div>
                    </div>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={(e) => {
                        // Only take first 3 characters and convert to uppercase
                        const value = e.target.value.substring(0, 3).toUpperCase();
                        setPostalCode(value);
                      }}
                      placeholder="Enter first 3 characters (e.g. V3R)"
                      className="w-full h-10 px-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      maxLength={3}
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      Only the first 3 characters (FSA) of the postal code are needed to determine service area availability
                    </div>
                  </div>
                )}

                {/* Calendar and Time Selection */}
                {selectedServices.length > 0 && (
                  <div className="pb-4 border-b border-gray-200">
                    {renderCalendar()}
                  </div>
                )}

                {/* Payment Summary */}
                {selectedServices.length > 0 && (
                  <div className="pb-4">
                    {renderPaymentSummary()}
                  </div>
                )}

                {/* Submit Button */}
                <div className="bg-white pt-4 mt-2">
                  <button
                    type="submit"
                    disabled={loading || !selectedCustomer || selectedServices.length === 0}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Creating...' : 'Create Booking'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {isSelectCustomerModalOpen && (
        <div className="fixed inset-0 z-[30000]" style={{pointerEvents: isSelectCustomerModalOpen ? 'auto' : 'none'}}>
          <SelectCustomerModal
            isOpen={isSelectCustomerModalOpen}
            onClose={() => setIsSelectCustomerModalOpen(false)}
            onSelect={customer => {
              console.log('📱 Customer selected in CreateMobileBookingModal:', customer);
              setSelectedCustomer(customer);
              setCurrentVehicle(null);
              setIsSelectCustomerModalOpen(false);
              
              // Save progress immediately with the new customer data
              saveMobileBookingProgress({ selectedCustomer: customer });
            }}
          />
        </div>
      )}

      {isSelectVehicleModalOpen && (
        <div className="fixed inset-0 z-[30000]" style={{pointerEvents: isSelectVehicleModalOpen ? 'auto' : 'none'}}>
          <SelectVehicleModal
            isOpen={isSelectVehicleModalOpen}
            onClose={() => setIsSelectVehicleModalOpen(false)}
            onSelect={handleSelectVehicle}
            vehicles={selectedCustomer?.vehicles || []}
            selectedVehicles={selectedServices.map(s => s.vehicle)}
            onAddVehicle={() => {
              setIsSelectVehicleModalOpen(false);
              setIsCreateVehicleModalOpen(true);
            }}
          />
        </div>
      )}

      {isCreateVehicleModalOpen && (
        <div className="fixed inset-0 z-[30000]" style={{pointerEvents: isCreateVehicleModalOpen ? 'auto' : 'none'}}>
          <CreateVehicleModal
            isOpen={isCreateVehicleModalOpen}
            onClose={() => setIsCreateVehicleModalOpen(false)}
            onSuccess={handleVehicleCreated}
            customerId={selectedCustomer?.id || ''}
          />
        </div>
      )}

      {isEditCustomerModalOpen && (
        <div className="fixed inset-0 z-[30000]" style={{pointerEvents: isEditCustomerModalOpen ? 'auto' : 'none'}}>
          <EditCustomerModal
            isOpen={isEditCustomerModalOpen}
            onClose={() => setIsEditCustomerModalOpen(false)}
            onSuccess={(updatedCustomer) => {
              setIsEditCustomerModalOpen(false);
              setSelectedCustomer(updatedCustomer);
            }}
            customer={selectedCustomer}
          />
        </div>
      )}

      {isSelectServiceModalOpen && (
        <div className="fixed inset-0 z-[30000]" style={{pointerEvents: isSelectServiceModalOpen ? 'auto' : 'none'}}>
          <SelectServiceModal
            isOpen={isSelectServiceModalOpen}
            onClose={() => setIsSelectServiceModalOpen(false)}
            onAdd={async (service, priceOption) => {
              handleAddService(service, priceOption);
              return Promise.resolve();
            }}
          />
        </div>
      )}

      {isEditServiceModalOpen && (
        <div className="fixed inset-0 z-[30000]" style={{pointerEvents: isEditServiceModalOpen ? 'auto' : 'none'}}>
          <EditServiceModal
            isOpen={isEditServiceModalOpen}
            onClose={() => {
              setIsEditServiceModalOpen(false);
              setSelectedServiceToEdit(null);
            }}
            onSave={async (updatedService) => {
              if (!selectedServiceToEdit) return;
              
              // Update the service in the selectedServices array
              setSelectedServices(prev => prev.map(s => 
                s === selectedServiceToEdit
                  ? {
                      ...s,
                      service: {
                        ...s.service,
                        name: updatedService.name,
                        description: updatedService.description,
                        tax_exempt: updatedService.tax_exempt,
                        tax_rate: updatedService.tax_rate
                      },
                      priceOption: {
                        ...s.priceOption,
                        price: updatedService.price,
                        duration_minutes: updatedService.duration_minutes
                      }
                    }
                  : s
              ));

              // Update the add-ons for this service
              if (updatedService.addons) {
                setSelectedServiceAddons(prev => ({
                  ...prev,
                  [selectedServiceToEdit.service.id]: updatedService.addons
                }));
              }

              setIsEditServiceModalOpen(false);
              setSelectedServiceToEdit(null);
            }}
            service={selectedServiceToEdit ? {
              name: selectedServiceToEdit.service.name,
              description: selectedServiceToEdit.service.description,
              price: selectedServiceToEdit.priceOption.price,
              duration_minutes: selectedServiceToEdit.priceOption.duration_minutes,
              is_active: true,
              tax_rate: selectedServiceToEdit.service.tax_rate || 5,
              tax_exempt: selectedServiceToEdit.service.tax_exempt || false,
              addons: selectedServiceAddons[selectedServiceToEdit.service.id] || []
            } : null}
          />
        </div>
      )}

      {isEditVehicleModalOpen && (
        <div className="fixed inset-0 z-[30000]" style={{pointerEvents: isEditVehicleModalOpen ? 'auto' : 'none'}}>
          <EditVehicleModal
            isOpen={isEditVehicleModalOpen}
            onClose={() => {
              setIsEditVehicleModalOpen(false);
              setSelectedVehicleToEdit(null);
            }}
            vehicle={selectedVehicleToEdit}
            onUpdate={async (updatedVehicle: CustomerVehicle) => {
              // Update customer data to get the updated vehicle
              const updatedCustomer = await getCustomer(selectedCustomer?.id || '');
              setSelectedCustomer(updatedCustomer);
              
              // Update the vehicle in selectedServices
              setSelectedServices(prev => prev.map(s => 
                s.vehicle.id === updatedVehicle.id
                  ? { ...s, vehicle: updatedVehicle }
                  : s
              ));
              
              setIsEditVehicleModalOpen(false);
              setSelectedVehicleToEdit(null);
            }}
          />
        </div>
      )}

      {isSelectAddonsModalOpen && (
        <div className="fixed inset-0 z-[30000]" style={{pointerEvents: isSelectAddonsModalOpen ? 'auto' : 'none'}}>
          <SelectServiceAddonsModal
            isOpen={isSelectAddonsModalOpen}
            onClose={() => {
              setIsSelectAddonsModalOpen(false);
              setCurrentService(null);
            }}
            serviceId={currentService?.id || ''}
            onSelect={handleAddAddons}
            selectedAddons={currentService ? selectedServiceAddons[currentService.id] || [] : []}
          />
        </div>
      )}

      {isResourceSelectorOpen && (
        <div className="fixed inset-0 z-[30000]" style={{pointerEvents: isResourceSelectorOpen ? 'auto' : 'none'}}>
          <ResourceSelector />
        </div>
      )}
    </>
  );
} 